import { useMemo } from 'react';
import { Editor } from '@tiptap/react';
import { isNil, uniq } from 'lodash';

import { Icon, IconName } from '@/components/icon';
import { getDomain } from '@/public/utils/url';

import { CloseButton } from '@/components/button';

import { getIcon } from './utils';

import styles from './footnote.module.scss';

interface Footnote {
  citeId: string;
  label: string;
  pages: string;
  href: string;
  icon: IconName;
  num: number;
}

interface GroupedCitationsPopoverProps {
  groupCiteIds: string[];
  editor: Editor;
  onClose: () => void;
}

export const GroupedCitationsPopover = ({
  groupCiteIds,
  editor,
  onClose,
}: GroupedCitationsPopoverProps) => {
  const footnotes = useMemo(() => {
    const result: Footnote[] = [];

    editor.state.doc.descendants((node) => {
      const citeId = node.attrs.citeId;
      if (
        node.type.name === 'footnoteNode' &&
        groupCiteIds.includes(citeId) &&
        result.every((f) => f.citeId !== citeId)
      ) {
        const href = node.attrs.href;
        const domain = isNil(href) || href.length === 0 ? null : getDomain(href);

        if (domain !== null) {
          result.push({
            href,
            citeId,
            pages: node.attrs.pages,
            label: node.attrs.label ?? `Link to ${domain}`,
            icon: getIcon(domain),
            num: node.attrs.num,
          });
        }
      }
    });

    return result;
  }, [groupCiteIds, editor.state.doc]);

  const uniqueHostIcons = useMemo(() => uniq(footnotes.map((note) => note.icon)), [footnotes]);

  return (
    <div className={styles.groupedCitationsPopover}>
      <div className={styles.popoverHeader}>
        {footnotes.length} {footnotes.length === 1 ? 'source' : 'sources'}
        <div className={styles.popoverSources}>
          {uniqueHostIcons.map((icon) => (
            <Icon key={icon} name={icon} size={16} />
          ))}
        </div>
        <CloseButton className={styles.popoverClose} onClick={onClose} />
      </div>
      <ol className={styles.popoverList}>
        {footnotes.map((footnote) => {
          const pages =
            footnote.pages !== undefined ? String(footnote.pages).split(',') : undefined;
          return (
            <li key={footnote.citeId} className={styles.popoverItem} data-num={footnote.num}>
              <a
                href={footnote.href}
                target="_blank"
                rel="noopener noreferrer nofollow"
                draggable={false}
                className={styles.listItem}>
                <Icon name={footnote.icon} size={20} />
                <div className={styles.popoverItemContent}>
                  <span className={styles.footnoteLabel}>{footnote.label}</span>
                  {pages && pages.length > 0 && (
                    <>
                      <span className={styles.pageNumber}>
                        Page{pages.length > 1 ? 's' : ''} {pages.join(', ')}
                      </span>
                    </>
                  )}
                </div>
              </a>
            </li>
          );
        })}
      </ol>
    </div>
  );
};
